import { useState, useEffect } from 'react';
import { cufeConPrefijo } from '../utils/cufe';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardBody, Button, Spinner, Alert, DropdownMenu } from '@components/ui';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@components/ui';
import type { DropdownMenuItem } from '@components/ui';
import { formatNumber, formatPriceDisplay } from '@/utils/format';
import { showSuccess, showError, showWarning, showConfirm, showLoading, closeAlert } from '@/utils/sweetAlert';
import { useReportError } from '@features/support/SupportErrorProvider';
import {
  ArrowLeftIcon,
  DocumentDuplicateIcon,
  EnvelopeIcon,
  PrinterIcon,
  EllipsisVerticalIcon,
  DocumentTextIcon,
  XMarkIcon,
  DocumentMinusIcon,
  DocumentPlusIcon,
  ArrowDownTrayIcon,
  WrenchScrewdriverIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useInvoice } from '../hooks/useInvoices';
import { invoiceService } from '../services/invoiceService';
import { useCompanies } from '@features/companies/hooks/useCompanies';
import { QRCodeSVG } from 'qrcode.react';
import { ResendEmailModal } from './ResendEmailModal';
import { OTI_CODES } from '@features/products/constants/taxCategories';
import type { EmailAttachments, EmailReceiver } from '../types/invoice.types';
import { trackingHelpers } from '@/services/mixpanel';

// Mapeo de tipos de documento
const documentTypes: Record<string, string> = {
  '01': 'Factura de Operación Interna',
  '02': 'Factura de Importación',
  '03': 'Factura de Exportación',
  '04': 'Nota de Crédito Referente a una o Varias FE',
  '05': 'Nota de Débito Referente a una o Varias FE',
  '06': 'Nota de Crédito Genérica',
  '07': 'Nota de Débito Genérica',
  '08': 'Factura de Zona Franca',
  '09': 'Reembolso',
  '10': 'Factura de operación extranjera',
};

// Mapeo de formas de pago
const paymentMethods: Record<string, string> = {
  '01': 'Crédito',
  '02': 'Efectivo',
  '03': 'Tarjeta de crédito',
  '04': 'Tarjeta de débito',
  '05': 'Tarjeta Fidelización',
  '06': 'Vale',
  '07': 'Tarjeta de regalo',
  '08': 'Transf./Depósito a cta. Bancaria',
  '09': 'Cheque',
  '10': 'Punto de pago',
  '99': 'Otro',
};

// Mapeo de tasas de ITBMS (códigos a porcentajes)
const itbmsTaxRates: Record<string, number> = {
  '00': 0,    // No aplica ITBMS
  '01': 7,    // ITBMS Tasa 7%
  '02': 10,   // ITBMS Tasa 10%
  '03': 15,   // ITBMS Tasa 15%
  '04': 5,    // ITBMS Tasa 5%
};

// Mapeo de códigos de retención
const retentionCodes: Record<string, { description: string; percentage: string }> = {
  '1': { description: 'Pago por servicio profesional al estado', percentage: '100%' },
  '2': { description: 'Pago por venta de bienes/servicios al estado', percentage: '50%' },
  '3': { description: 'Pago a no domiciliado o empresa exterior', percentage: '100%' },
  '4': { description: 'Pago por compra de bienes/servicios', percentage: '50%' },
  '7': { description: 'Pago a comercio afiliado TC/TD', percentage: '50%' },
  '8': { description: 'Otros (disminución de la retención)', percentage: 'Variable' },
};

// Mock data para la factura
/*
const _mockInvoiceDetail = {
  id: '57bb0e6e-9b9b-4487-9c0f-a67fbea10dff',
  invoiceNumber: 'FE-2024-001',
  cufe: 'FE123456789012345678901234567890123456789012',
  authProtocol: 'PROT-2024-001',
  documentType: 'Factura Electrónica',
  status: 'approved', // approved, pending, rejected, draft
  statusMessage: 'Autorizado el uso de la FE',
  issueDate: '2024-10-15',
  dueDate: '2024-11-15',

  // Emisor
  issuer: {
    name: 'Empresa Demo S.A.',
    ruc: '0000000-0-000000',
    dv: '00',
    address: 'Calle Principal, Edificio Corporativo',
    city: 'Ciudad de Panamá',
    country: 'Panamá',
    branchOffice: 'Sucursal Principal',
    phone: '+507 200-0000',
  },

  // Receptor
  receiver: {
    name: 'Tech Solutions S.A.',
    ruc: '1234567-1-123456',
    dv: '12',
    address: 'Calle 50, Edificio Plaza 2000, Piso 10',
    city: 'Ciudad de Panamá',
    country: 'Panamá',
    email: 'contacto@techsolutions.com',
    phone: '+507 300-1234',
  },

  // Items
  items: [
    {
      id: '1',
      code: 'PROD-001',
      name: 'Laptop Dell Inspiron 15',
      description: 'Laptop Dell Inspiron 15, Intel i5, 8GB RAM, 256GB SSD',
      quantity: 2,
      unitPrice: 750.00,
      discount: 0,
      taxRate: 7,
      subtotal: 1500.00,
      taxAmount: 105.00,
      total: 1605.00,
    },
    {
      id: '2',
      code: 'PROD-002',
      name: 'Monitor Samsung 24"',
      description: 'Monitor Samsung 24 pulgadas, Full HD, HDMI',
      quantity: 3,
      unitPrice: 180.00,
      discount: 5,
      taxRate: 7,
      subtotal: 540.00,
      taxAmount: 37.80,
      total: 577.80,
    },
  ],

  // Pagos
  paymentMethods: [
    { method: 'Transferencia Bancaria', amount: 2182.80 },
  ],

  // Totales
  subtotal: 2040.00,
  totalDiscount: 27.00,
  totalTax: 142.80,
  grandTotal: 2182.80,
  currency: 'USD',

  qrCode: '/images/No-image-available.png',
};
*/

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const reportError = useReportError();

  // Muestra el error en el modal con Asistente IA (para acciones reales: anular, CAFE, email).
  const showErrorWithAssist = (title: string, message: string) => {
    reportError({ title, message, screen: 'ver detalles de factura' });
  };

  const { data: invoiceData, isLoading, error } = useInvoice(id || '');
  const { selectedCompany, isLoadingSelectedCompany, refetchSelectedCompany } = useCompanies({ navigateOnSelect: false });

  // Ensure selected company is loaded when page mounts
  useEffect(() => {
    if (!selectedCompany && !isLoadingSelectedCompany) {
      refetchSelectedCompany();
    }
  }, [selectedCompany, isLoadingSelectedCompany, refetchSelectedCompany]);
  const [isGeneratingCAFE, setIsGeneratingCAFE] = useState(false);
  const [cafeUrl, setCAfeUrl] = useState<string | null>(null);
  const [isResendEmailModalOpen, setIsResendEmailModalOpen] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);

  // Track invoice viewed
  useEffect(() => {
    if (invoiceData && invoiceData.id) {
      const isCorrection = invoiceData.documentType === '04' || invoiceData.documentType === '05';
      const totalAmount = (invoiceData as any).grandTotal || (invoiceData as any).total || 0;

      if (isCorrection) {
        // Track credit/debit note viewed
        trackingHelpers.trackCreditNoteView(
          invoiceData.id,
          invoiceData.invoiceNumber,
          totalAmount,
          selectedCompany?.id || 0
        );
      } else {
        // Track regular invoice viewed
        trackingHelpers.trackInvoiceView(
          invoiceData.id,
          invoiceData.invoiceNumber,
          invoiceData.documentType || '01',
          totalAmount,
          selectedCompany?.id || 0,
          false
        );
      }
    }
  }, [invoiceData, selectedCompany]);

  // Handlers para las acciones del menú
  const handleCreateCAFE = async () => {
    if (isLoadingSelectedCompany) {
      showWarning('Cargando...', 'Por favor espera mientras se carga la información de la empresa.');
      return;
    }
    if (!id || !selectedCompany?.id) {
      showError('Error', 'No se pudo obtener la información necesaria para generar el CAFE');
      return;
    }

    const result = await showConfirm(
      '¿Generar CAFE?',
      '¿Estás seguro de que deseas generar el CAFE para esta factura?'
    );

    if (result.isConfirmed) {
      setIsGeneratingCAFE(true);
      showLoading('Generando CAFE...', 'Por favor espera mientras se genera el documento.');
      try {
        const result = await invoiceService.generateCAFE(id, selectedCompany.id, {
          templateId: 'default',
          sendEmail: true,
        });
        closeAlert();

        if (result.success && result.cafeUrl) {
          setCAfeUrl(result.cafeUrl);
          await showSuccess(
            '¡CAFE Generado!',
            'El CAFE se ha generado exitosamente y se ha enviado un correo con el documento.'
          );
        } else {
          showWarning('CAFE Generado', result.message || 'CAFE generado pero no se obtuvo la URL del documento');
        }
      } catch (error: any) {
        closeAlert();
        console.error('Error generating CAFE:', error);
        showErrorWithAssist('Error al generar CAFE', error.message || 'Por favor, intenta de nuevo.');
      } finally {
        setIsGeneratingCAFE(false);
      }
    }
  };

  const handlePrintCAFE = async () => {
    if (isLoadingSelectedCompany) {
      showWarning('Cargando...', 'Por favor espera mientras se carga la información de la empresa.');
      return;
    }
    if (!id || !selectedCompany?.id) {
      showError('Error', 'No se pudo obtener la información necesaria');
      return;
    }

    // Si ya existe el CAFE, abrirlo directo
    const existingUrl = invoiceData?.cafe || cafeUrl;
    if (existingUrl) {
      window.open(existingUrl, '_blank');
      return;
    }

    // Si no existe, generarlo primero
    setIsGeneratingCAFE(true);
    showLoading('Generando CAFE...', 'Por favor espera mientras se genera el documento.');
    try {
      const result = await invoiceService.generateCAFE(id, selectedCompany.id, {
        templateId: 'default',
        sendEmail: true,
      });
      closeAlert();

      if (result.success && result.cafeUrl) {
        setCAfeUrl(result.cafeUrl);
        window.open(result.cafeUrl, '_blank');
      } else {
        showError('Error', result.message || 'No se pudo generar el CAFE');
      }
    } catch (error: any) {
      closeAlert();
      showError('Error al generar CAFE', error.message || 'Por favor, intenta de nuevo.');
    } finally {
      setIsGeneratingCAFE(false);
    }
  };

  // Check if invoice can be cancelled (within 7 days of creation)
  const canCancelInvoice = () => {
    if (!invoiceData?.createdDate) return false;

    const createdDate = new Date(invoiceData.createdDate);
    const currentDate = new Date();
    const daysDifference = Math.floor((currentDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));

    return daysDifference <= 7;
  };

  const handleCancelInvoice = async () => {
    if (isLoadingSelectedCompany) {
      showWarning('Cargando...', 'Por favor espera mientras se carga la información de la empresa.');
      return;
    }
    if (!id || !selectedCompany?.id || !selectedCompany?.code) {
      showError('Error', 'No se pudo obtener la información necesaria para anular la factura');
      return;
    }

    // Check if invoice can be cancelled
    if (!canCancelInvoice()) {
      showWarning(
        'No se puede anular',
        'Solo se pueden anular facturas dentro de los primeros 7 días de su creación.'
      );
      return;
    }

    const result = await showConfirm(
      '¿Anular Factura?',
      '¿Estás seguro de que deseas anular esta factura? Esta acción no se puede deshacer.'
    );

    if (result.isConfirmed) {
      showLoading('Anulando Factura...', 'Por favor espera mientras se procesa la anulación.');
      try {
        const cancelResult = await invoiceService.cancelInvoice(
          id,
          selectedCompany.id,
          selectedCompany.code,
          'Anulación de factura'
        );
        closeAlert();

        if (cancelResult.success) {
          await showSuccess(
            '¡Factura Anulada!',
            cancelResult.message || 'La factura se ha anulado exitosamente.'
          );
          // Reload the page to reflect the updated status
          window.location.reload();
        } else {
          showErrorWithAssist('Error al anular', cancelResult.message || 'No se pudo anular la factura');
        }
      } catch (error: any) {
        closeAlert();
        console.error('Error canceling invoice:', error);
        showErrorWithAssist('Error al anular factura', error.message || 'Por favor, intenta de nuevo.');
      }
    }
  };

  const handleCreateCreditNote = () => {
    if (!id) {
      showError('Error', 'No se pudo obtener el ID de la factura');
      return;
    }
    console.log('Crear Nota de Crédito para factura:', id);
    // Navigate to wizard with query params: type=credit, ref=invoiceId
    navigate(`/invoices/new?type=credit&ref=${id}`);
  };

  const handleCreateDebitNote = () => {
    if (!id) {
      showError('Error', 'No se pudo obtener el ID de la factura');
      return;
    }
    console.log('Crear Nota de Débito para factura:', id);
    // Navigate to wizard with query params: type=debit, ref=invoiceId
    navigate(`/invoices/new?type=debit&ref=${id}`);
  };

  const handleCorrectionSameCufe = () => {
    if (!id) {
      showError('Error', 'No se pudo obtener el ID de la factura');
      return;
    }
    // Navigate to wizard with correction mode (same CUFE)
    navigate(`/invoices/new?mode=correction&sameCufe=true&ref=${id}`);
  };

  const handleCorrectionNewCufe = () => {
    if (!id) {
      showError('Error', 'No se pudo obtener el ID de la factura');
      return;
    }
    // Navigate to wizard with correction mode (new CUFE)
    navigate(`/invoices/new?mode=correction&sameCufe=false&ref=${id}`);
  };

  const handleGoBack = () => {
    // Check if there's navigation state indicating where we came from
    const fromPath = location.state?.from;

    if (fromPath) {
      // If we have a "from" path in the state, navigate back to it
      navigate(fromPath);
    } else {
      // Otherwise, try to use browser history or default to main invoices
      navigate(-1);
    }
  };

  const handleResendEmail = async (attachments: EmailAttachments, receivers: EmailReceiver[]) => {
    if (isLoadingSelectedCompany) {
      showWarning('Cargando...', 'Por favor espera mientras se carga la información de la empresa.');
      return;
    }
    if (!id || !selectedCompany?.id) {
      showError('Error', 'No se pudo obtener la información necesaria');
      return;
    }

    setIsResendingEmail(true);

    try {
      const result = await invoiceService.resendInvoiceEmail(
        id,
        selectedCompany.id,
        attachments,
        receivers
      );

      setIsResendingEmail(false);
      setIsResendEmailModalOpen(false);

      if (result.success) {
        await showSuccess('¡Email Enviado!', result.message || 'El email se ha enviado exitosamente');
      } else {
        showErrorWithAssist('Error al enviar', result.message || 'Error al enviar el email');
      }
    } catch (error) {
      setIsResendingEmail(false);
      setIsResendEmailModalOpen(false);
      showErrorWithAssist('Error al enviar', 'Ocurrió un error inesperado al enviar el email');
    }
  };

  const handleResendToDGI = async () => {
    if (!id) {
      showError('Error', 'No se pudo obtener el ID de la factura');
      return;
    }

    const result = await showConfirm(
      '¿Reenviar a DGI?',
      '¿Estás seguro de que deseas reenviar esta factura a la DGI?'
    );

    if (result.isConfirmed) {
      showLoading('Reenviando a DGI...', 'Por favor espera mientras se procesa el reenvío.');
      try {
        const resendResult = await invoiceService.resendToDGI(id);
        closeAlert();

        if (!resendResult.success) {
          showError('Error al reenviar', resendResult.responseMessage || 'No se pudo reenviar la factura a la DGI');
          return;
        }

        const code = resendResult.responseCode;
        const msg = `Código: ${code} - ${resendResult.responseMessage || ''}`;

        if (code === '0920') {
          await showSuccess('Factura Procesada', msg);
          window.location.reload();
        } else if (code === '0260') {
          await showSuccess('Factura Autorizada', msg);
          window.location.reload();
        } else {
          await showWarning(
            'Factura sigue encolada',
            code
              ? `La DGI respondió con código ${code}: ${resendResult.responseMessage || 'Sin mensaje'}. La factura no fue procesada aún.`
              : 'La DGI no devolvió un código de respuesta. La factura sigue encolada.'
          );
        }
      } catch (error: any) {
        closeAlert();
        showError('Error al reenviar', error.message || 'Por favor, intenta de nuevo.');
      }
    }
  };

  // Determinar si la factura tiene error (eInvoiceStatus === 1)
  const isErrorInvoice = invoiceData?.eInvoiceStatus === 1;

  // Menú de acciones - diferente según el estado de la factura
  const actionMenuItems: DropdownMenuItem[] = isErrorInvoice
    ? [
      // Para facturas con error: solo opciones de corrección
      {
        label: 'Hacer corrección (mismo CUFE)',
        icon: WrenchScrewdriverIcon,
        onClick: handleCorrectionSameCufe,
        variant: 'default',
      },
      {
        label: 'Hacer corrección (nuevo CUFE)',
        icon: WrenchScrewdriverIcon,
        onClick: handleCorrectionNewCufe,
        variant: 'default',
      },
    ]
    : [
      // Para facturas normales: todas las opciones
      {
        label: isGeneratingCAFE ? 'Generando CAFE...' : (cafeUrl ? 'Ver CAFE' : 'Crear CAFE'),
        icon: cafeUrl ? ArrowDownTrayIcon : DocumentTextIcon,
        onClick: cafeUrl
          ? () => window.open(cafeUrl, '_blank')
          : handleCreateCAFE,
        variant: 'default',
        disabled: isGeneratingCAFE,
      },
      {
        label: 'Reenviar Email',
        icon: EnvelopeIcon,
        onClick: () => {
          console.log('🔵 Reenviar Email clicked');
          setIsResendEmailModalOpen(true);
        },
        variant: 'default',
      },
      {
        label: isGeneratingCAFE ? 'Generando...' : 'Imprimir CAFE',
        icon: PrinterIcon,
        onClick: handlePrintCAFE,
        variant: 'default',
        disabled: isGeneratingCAFE,
      },
      {
        label: 'Reenviar a DGI',
        icon: ArrowPathIcon,
        onClick: handleResendToDGI,
        variant: 'default',
        disabled: invoiceData?.eInvoiceStatus !== 2,
      },
      {
        label: canCancelInvoice() ? 'Anular Factura' : 'Anular Factura (solo 7 días)',
        icon: XMarkIcon,
        onClick: handleCancelInvoice,
        variant: 'danger',
        disabled: !canCancelInvoice(),
      },
      {
        divider: true,
        label: '',
        onClick: () => { },
      },
      {
        label: 'Nota de Crédito',
        icon: DocumentMinusIcon,
        onClick: handleCreateCreditNote,
        variant: 'default',
      },
      {
        label: 'Nota de Débito',
        icon: DocumentPlusIcon,
        onClick: handleCreateDebitNote,
        variant: 'default',
      },
      {
        divider: true,
        label: '',
        onClick: () => { },
      },
      {
        label: 'Clonar Factura',
        icon: DocumentDuplicateIcon,
        onClick: () => navigate(`/invoices/new?mode=clone&ref=${id}`),
        variant: 'default',
      },
    ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !invoiceData) {
    return (
      <Alert variant="danger" title="Error al cargar la factura">
        No se pudo cargar la información de la factura. Por favor, intenta de nuevo.
      </Alert>
    );
  }

  // Extract data from the payload
  const payload = invoiceData.payload || {};

  return (
    <div className="space-y-6">
      {/* CAFE Success Alert */}
      {cafeUrl && (
        <Alert variant="success" title="CAFE Generado Exitosamente">
          <div className="flex items-center justify-between">
            <p>El CAFE se ha generado correctamente y está disponible para descargar.</p>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
              onClick={() => window.open(cafeUrl, '_blank')}
            >
              Descargar CAFE
            </Button>
          </div>
        </Alert>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
            {payload.dGen?.iDoc ? documentTypes[payload.dGen.iDoc] || 'Detalle de Factura' : 'Detalle de Factura'}
          </h1>
          <p className="mt-2 text-secondary-600 dark:text-secondary-400">
            Document Number: {invoiceData.billNumber || 'N/A'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Menú de acciones */}
          <DropdownMenu
            trigger={
              <Button data-guide="Acciones Factura" variant="outline" leftIcon={<EllipsisVerticalIcon className="w-5 h-5" />}>
                Acciones
              </Button>
            }
            items={actionMenuItems}
          />
          <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />} onClick={handleGoBack}>
            Volver
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left Side */}
        <div className="lg:col-span-2 space-y-6">
          {/* Issuer & Receiver */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Emisor */}
            <Card>
              <CardBody>
                <h3 className="text-lg font-bold text-center mb-4 pb-2 border-b border-secondary-200 dark:border-secondary-700">
                  Información del Emisor
                </h3>
                <div className="space-y-3 text-center">
                  <div>
                    <p className="font-semibold text-secondary-900 dark:text-white">De:</p>
                    <p className="text-secondary-700 dark:text-secondary-300">{payload.dGen?.gEmis?.dNombEm || 'N/A'}</p>
                  </div>
                  <hr className="dark:border-secondary-700" />
                  <div>
                    <p className="font-semibold text-secondary-900 dark:text-white">Ubicación:</p>
                    <p className="text-sm text-secondary-600 dark:text-secondary-400">
                      {payload.dGen?.gEmis?.dDirecEm || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-secondary-900 dark:text-white">RUC:</p>
                    <p className="text-secondary-700 dark:text-secondary-300">
                      {payload.dGen?.gEmis?.gRucEmi?.dRuc || 'N/A'} {payload.dGen?.gEmis?.gRucEmi?.dDV && <><strong>DV:</strong> {payload.dGen.gEmis.gRucEmi.dDV}</>}
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-secondary-900 dark:text-white">Sucursal:</p>
                    <p className="text-secondary-700 dark:text-secondary-300">{payload.dGen?.gEmis?.dSucEm || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-secondary-900 dark:text-white">Teléfono:</p>
                    <p className="text-secondary-700 dark:text-secondary-300">{payload.dGen?.gEmis?.dTfnEm || 'N/A'}</p>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Receptor */}
            <Card>
              <CardBody>
                <h3 className="text-lg font-bold text-center mb-4 pb-2 border-b border-secondary-200 dark:border-secondary-700">
                  Información del Receptor
                </h3>
                <div className="space-y-3 text-center">
                  <div>
                    <p className="font-semibold text-secondary-900 dark:text-white">Para:</p>
                    <p className="text-secondary-700 dark:text-secondary-300">{payload.dGen?.gDatRec?.dNombRec || 'N/A'}</p>
                  </div>
                  <hr className="dark:border-secondary-700" />
                  <div>
                    <p className="font-semibold text-secondary-900 dark:text-white">Ubicación:</p>
                    <p className="text-sm text-secondary-600 dark:text-secondary-400">
                      {payload.dGen?.gDatRec?.dDirecRec || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-secondary-900 dark:text-white">RUC:</p>
                    <p className="text-secondary-700 dark:text-secondary-300">
                      {payload.dGen?.gDatRec?.gRucRec?.dRuc || 'N/A'} {payload.dGen?.gDatRec?.gRucRec?.dDV && <><strong>DV:</strong> {payload.dGen.gDatRec.gRucRec.dDV}</>}
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-secondary-900 dark:text-white">Correo:</p>
                    <p className="text-secondary-700 dark:text-secondary-300">{payload.dGen?.gDatRec?.dCorElectRec || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-secondary-900 dark:text-white">Teléfono:</p>
                    <p className="text-secondary-700 dark:text-secondary-300">{payload.dGen?.gDatRec?.dTfnRec || 'N/A'}</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Products List */}
          <Card>
            <CardBody className="p-0">
              <div className="p-4 bg-secondary-50 dark:bg-secondary-800">
                <h3 className="text-lg font-bold text-center text-secondary-900 dark:text-white">
                  Lista de Productos
                </h3>
              </div>
              {payload.gItem && payload.gItem.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead>Cant.</TableHead>
                      <TableHead>Precio Unit.</TableHead>
                      <TableHead>Desc.</TableHead>
                      <TableHead>ITBMS</TableHead>
                      <TableHead>ISC</TableHead>
                      <TableHead>OTI</TableHead>
                      <TableHead>Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payload.gItem.map((item: any, index: number) => {
                      // Obtener el código de tasa ITBMS y convertirlo al porcentaje real
                      const taxCode = item.gITBMSItem?.dTasaITBMS || '00';
                      const taxRate = itbmsTaxRates[taxCode] || 0;
                      const taxAmount = parseFloat(item.gITBMSItem?.dValITBMS || 0);

                      // ISC
                      const iscRate = parseFloat(item.gISCItem?.dTasaISC || 0);
                      const iscAmount = parseFloat(item.gISCItem?.dValISC || 0);

                      // OTI
                      const otiItems = item.gOTIItem || [];

                      return (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{item.dCodProd || 'N/A'}</TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{item.dDescProd || 'N/A'}</div>
                            </div>
                          </TableCell>
                          <TableCell>{formatNumber(parseFloat(item.dCantCodInt) || 0, 2)}</TableCell>
                          <TableCell>${formatPriceDisplay(parseFloat(item.gPrecios?.dPrUnit || 0))}</TableCell>
                          <TableCell>{formatPriceDisplay(parseFloat(item.gPrecios?.dPrUnitDesc || 0))}</TableCell>
                          <TableCell>
                            <div className="text-xs text-secondary-500">{taxRate}%</div>
                            <div>${formatPriceDisplay(taxAmount)}</div>
                          </TableCell>
                          <TableCell>
                            {iscAmount > 0 ? (
                              <>
                                <div className="text-xs text-secondary-500">{iscRate}%</div>
                                <div>${formatPriceDisplay(iscAmount)}</div>
                              </>
                            ) : (
                              <span className="text-secondary-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {otiItems.length > 0 ? (
                              <div className="min-w-[100px]">
                                {otiItems.map((oti: any, otiIdx: number) => {
                                  const otiCode = OTI_CODES.find(c => c.code === oti.dCodOTI);
                                  const otiValue = parseFloat(oti.dValOTI || 0);
                                  return (
                                    <div key={otiIdx} className="text-xs text-secondary-500">
                                      {otiCode?.code}: ${formatNumber(otiValue)}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-secondary-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="font-semibold">${formatPriceDisplay(parseFloat(item.gPrecios?.dValTotItem || 0))}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-8 text-center text-secondary-500 dark:text-secondary-400">
                  No hay productos en esta factura
                </div>
              )}
            </CardBody>
          </Card>

          {/* Payment Methods & Totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Payment Methods */}
            <Card>
              <CardBody>
                <h3 className="text-lg font-bold text-center mb-4 pb-2 border-b border-secondary-200 dark:border-secondary-700">
                  Formas de Pago
                </h3>
                <table className="w-full">
                  <tbody>
                    {(payload.gTot?.gFormaPago || []).map((payment: any, index: number) => {
                      const paymentCode = payment.iFormaPago;
                      const paymentDescription = paymentMethods[paymentCode] || payment.dFormaPagoDesc || paymentCode;

                      return (
                        <tr key={index} className="border-b dark:border-secondary-700">
                          <td className="py-2 text-left text-secondary-700 dark:text-secondary-300">
                            {paymentDescription}
                            {paymentCode && paymentMethods[paymentCode] && (
                              <span className="text-xs text-secondary-500 dark:text-secondary-400 ml-1">
                                ({paymentCode})
                              </span>
                            )}
                          </td>
                          <td className="py-2 text-right font-semibold text-secondary-900 dark:text-white">
                            USD ${formatNumber(parseFloat(payment.dVlrCuota || 0))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardBody>
            </Card>

            {/* Totals */}
            <Card>
              <CardBody>
                <h3 className="text-lg font-bold text-center mb-4 pb-2 border-b border-secondary-200 dark:border-secondary-700">
                  Totales
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-secondary-600 dark:text-secondary-400">Subtotal:</span>
                    <span className="font-medium">${formatNumber(parseFloat(payload.gTot?.dTotNeto || 0))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-600 dark:text-secondary-400">Descuentos:</span>
                    <span className="font-medium text-danger-600">-${formatNumber(parseFloat(payload.gTot?.dTotDesc || 0))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-600 dark:text-secondary-400">ITBMS:</span>
                    <span className="font-medium">${formatNumber(parseFloat(payload.gTot?.dTotITBMS || 0))}</span>
                  </div>

                  {/* Otros Impuestos (ISC + OTI) */}
                  {(parseFloat(payload.gTot?.dTotISC || 0) > 0 || (payload.gTot?.gOTITotal && payload.gTot.gOTITotal.length > 0)) && (
                    <div className="border-l-4 border-primary-400 pl-3 py-2 bg-primary-50 dark:bg-primary-900/20 rounded">
                      <div className="font-medium text-secondary-700 dark:text-secondary-300 mb-2 text-sm">
                        Otros Impuestos:
                      </div>

                      {/* ISC */}
                      {parseFloat(payload.gTot?.dTotISC || 0) > 0 && (
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-secondary-600 dark:text-secondary-400">ISC:</span>
                          <span className="font-medium">${formatNumber(parseFloat(payload.gTot?.dTotISC || 0))}</span>
                        </div>
                      )}

                      {/* OTI */}
                      {payload.gTot?.gOTITotal && payload.gTot.gOTITotal.length > 0 && (
                        <>
                          {payload.gTot.gOTITotal.map((oti: any, index: number) => {
                            const otiCode = OTI_CODES.find(c => c.code === oti.dCodOTITotal);
                            return (
                              <div key={index} className="flex justify-between text-sm mb-1">
                                <div className="flex-1">
                                  <span className="font-medium text-secondary-600 dark:text-secondary-400">
                                    {oti.dCodOTITotal}
                                  </span>
                                  <span className="text-xs text-secondary-500 dark:text-secondary-400 ml-2">
                                    {otiCode?.description || 'Otro'}
                                  </span>
                                </div>
                                <span className="font-medium ml-3">
                                  ${formatNumber(parseFloat(oti.dValOTITotal || 0))}
                                </span>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  )}

                  {/* Retención */}
                  {payload.gTot?.gRetenc && (payload.gTot.gRetenc.cCodRetenc || payload.gTot.gRetenc.dCodRet) && (
                    <div className="border-l-4 border-warning-400 pl-3 py-2 bg-warning-50 dark:bg-warning-900/20 rounded">
                      <div className="flex justify-between text-sm">
                        <div className="flex-1">
                          <span className="font-medium text-secondary-700 dark:text-secondary-300">
                            Retención ({retentionCodes[payload.gTot.gRetenc.cCodRetenc || payload.gTot.gRetenc.dCodRet]?.percentage || ''})
                          </span>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                            {retentionCodes[payload.gTot.gRetenc.cCodRetenc || payload.gTot.gRetenc.dCodRet]?.description || 'Retención aplicada'}
                          </p>
                        </div>
                        <span className="font-medium ml-3 text-warning-600 dark:text-warning-400">
                          ${formatNumber(parseFloat(payload.gTot.gRetenc.cValRetenc || payload.gTot.gRetenc.dValRet || 0))}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between text-xl font-bold pt-3 border-t border-secondary-200 dark:border-secondary-700">
                    <span>Total:</span>
                    <span className="text-primary-600">
                      USD ${formatNumber(parseFloat(payload.gTot?.dVTot || 0))}
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Sidebar - Right Side */}
        <div className="space-y-4">
          {/* General Info Header */}
          <Card>
            <CardBody className="p-3 bg-secondary-50 dark:bg-secondary-800 text-center">
              <h3 className="text-lg font-bold text-secondary-900 dark:text-white">
                Información General
              </h3>
            </CardBody>
          </Card>

          {/* QR Code */}
          <Card>
            <CardBody className="flex flex-col items-center p-4 gap-2">
              {invoiceData.qrCode ? (
                <>
                  <QRCodeSVG
                    value={invoiceData.qrCode}
                    size={192}
                    level="H"
                    includeMargin={true}
                    className="border-2 border-secondary-200 dark:border-secondary-600 rounded-lg"
                  />
                  {/* Solo mostrar "Ver en DGI" si NO es factura con error */}
                  {!isErrorInvoice && (
                    <a
                      href={invoiceData.qrCode}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline text-center"
                    >
                      Ver en DGI
                    </a>
                  )}
                </>
              ) : (
                <div className="w-48 h-48 bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center rounded-lg">
                  <span className="text-secondary-500 text-sm text-center px-2">QR Code no disponible</span>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Status */}
          <Card>
            <CardBody className="p-4">
              {invoiceData.eInvoiceStatus === 1 && (
                <div className="rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 p-3">
                  <p className="text-sm text-danger-800 dark:text-danger-200 text-center">
                    {[invoiceData.responseCode, invoiceData.resolution].filter(Boolean).join(' - ') || 'Error en procesamiento'}
                  </p>
                  <button
                    type="button"
                    aria-label="Consultar al Asistente IA por qué se rechazó esta factura"
                    onClick={() =>
                      reportError({
                        title: 'Factura rechazada por la DGI',
                        message: 'La factura fue rechazada. Validá el motivo y obtené la solución.',
                        details: [
                          {
                            code: String(invoiceData.responseCode || ''),
                            message: String(invoiceData.resolution || 'Error en procesamiento'),
                          },
                        ],
                        screen: 'detalle de factura rechazada',
                      })
                    }
                    className="group relative mt-3 w-full flex items-center gap-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white px-4 py-3 text-left shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2"
                  >
                    {/* Punto que “late” para llamar la atención */}
                    <span className="absolute right-3 top-3 flex h-2.5 w-2.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
                    </span>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15 text-lg">
                      ✨
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold leading-tight">
                        ¿Qué significa este error? Te ayudo
                      </span>
                      <span className="block text-xs text-white/80 leading-tight mt-0.5">
                        El Asistente IA te explica el motivo y cómo resolverlo
                      </span>
                    </span>
                    <svg className="h-5 w-5 shrink-0 text-white/80 transition-transform group-hover:translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                </div>
              )}
              {invoiceData.eInvoiceStatus === 5 && (
                <div className="rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 p-3">
                  <p className="text-sm text-danger-800 dark:text-danger-200 text-center">
                    {[invoiceData.responseCode, invoiceData.resolution].filter(Boolean).join(' - ') || 'Anulada'}
                  </p>
                </div>
              )}
              {invoiceData.eInvoiceStatus === 2 && (
                <div className="rounded-lg bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 p-3">
                  <p className="text-sm text-warning-800 dark:text-warning-200 text-center">
                    Encolada
                  </p>
                </div>
              )}
              {invoiceData.eInvoiceStatus === 4 && (
                <div className="rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 p-3">
                  <p className="text-sm text-primary-800 dark:text-primary-200 text-center">
                    Pendiente de subir
                  </p>
                </div>
              )}
              {invoiceData.eInvoiceStatus === 0 && (
                <div className="rounded-lg bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 p-3">
                  <p className="text-sm text-success-800 dark:text-success-200 text-center">
                    Procesado
                  </p>
                </div>
              )}
              {invoiceData.eInvoiceStatus === 3 && (
                <div className="rounded-lg bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 p-3">
                  <p className="text-sm text-warning-800 dark:text-warning-200 text-center">
                    Procesando
                  </p>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Actions */}
          <Card>
            <CardBody className="p-4 space-y-2">
              {isErrorInvoice ? (
                /* Para facturas con error: solo mostrar Ver CFU XML */
                invoiceData.xml && (
                  <Button
                    variant="outline"
                    className="w-full"
                    leftIcon={<DocumentDuplicateIcon className="w-5 h-5" />}
                    onClick={() => window.open(invoiceData.xml, '_blank')}
                  >
                    Ver CFU XML
                  </Button>
                )
              ) : (
                /* Para facturas normales: todas las acciones */
                <>
                  <Button
                    data-guide="Reenviar Email"
                    variant="primary"
                    className="w-full"
                    leftIcon={<EnvelopeIcon className="w-5 h-5" />}
                    onClick={() => {
                      console.log('🔵 Reenviar Email button clicked');
                      setIsResendEmailModalOpen(true);
                    }}
                  >
                    Reenviar Email
                  </Button>
                  <Button
                    data-guide="Imprimir CAFE"
                    variant="outline"
                    className="w-full"
                    leftIcon={<PrinterIcon className="w-5 h-5" />}
                    onClick={handlePrintCAFE}
                    disabled={isGeneratingCAFE}
                  >
                    {isGeneratingCAFE ? 'Generando...' : 'Imprimir CAFE'}
                  </Button>
                  {invoiceData.cafe && (
                    <Button
                      variant="outline"
                      className="w-full"
                      leftIcon={<PrinterIcon className="w-5 h-5" />}
                      onClick={() => window.open(invoiceData.cafe, '_blank')}
                    >
                      Ver CAFE (PDF)
                    </Button>
                  )}
                  {invoiceData.xml && (
                    <Button
                      variant="outline"
                      className="w-full"
                      leftIcon={<DocumentDuplicateIcon className="w-5 h-5" />}
                      onClick={() => window.open(invoiceData.xml, '_blank')}
                    >
                      Ver XML
                    </Button>
                  )}
                  {invoiceData.qrCode && (
                    <Button
                      variant="outline"
                      className="w-full"
                      leftIcon={<DocumentDuplicateIcon className="w-5 h-5" />}
                      onClick={() => window.open(invoiceData.qrCode, '_blank')}
                    >
                      Ver en DGI
                    </Button>
                  )}
                </>
              )}
            </CardBody>
          </Card>

          {/* Document Info */}
          <Card>
            <CardBody className="p-4 space-y-3 text-sm">
              <div>
                <p className="font-semibold text-secondary-900 dark:text-white">Tipo de documento:</p>
                <p className="text-secondary-700 dark:text-secondary-300">
                  {payload.dGen?.iDoc ? (
                    <>
                      {documentTypes[payload.dGen.iDoc] || payload.dGen.iDoc}
                      <span className="text-xs text-secondary-500 dark:text-secondary-400 ml-1">
                        ({payload.dGen.iDoc})
                      </span>
                    </>
                  ) : (
                    'N/A'
                  )}
                </p>
              </div>
              <div>
                <p className="font-semibold text-secondary-900 dark:text-white">N° de documento:</p>
                <p className="text-secondary-700 dark:text-secondary-300 break-all">{payload.dGen?.dNroDF || invoiceData.billNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="font-semibold text-secondary-900 dark:text-white">CUFE:</p>
                <p className="text-secondary-700 dark:text-secondary-300 break-all text-xs">
                  {cufeConPrefijo(invoiceData.cufe) || 'N/A'}
                </p>
              </div>
              <div>
                <p className="font-semibold text-secondary-900 dark:text-white">Protocolo de autorización:</p>
                <p className="text-secondary-700 dark:text-secondary-300 break-all">{invoiceData.authProtocol || 'N/A'}</p>
              </div>
              <div>
                <p className="font-semibold text-secondary-900 dark:text-white">Fecha de emisión:</p>
                <p className="text-secondary-700 dark:text-secondary-300">
                  {payload.dGen?.dFechaEm ? new Date(payload.dGen.dFechaEm).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Modal de Reenvío de Email */}
      <ResendEmailModal
        isOpen={isResendEmailModalOpen}
        onClose={() => setIsResendEmailModalOpen(false)}
        onSubmit={handleResendEmail}
        isSubmitting={isResendingEmail}
      />
    </div>
  );
}
